import * as React from 'react';

import type {EntitySummaryRating} from 'nutshell-graphql-types';
import type {GetLeadContextSummary_leadContextSummary as LeadContextSummary} from 'nutshell-graphql-types';

import {useGenerateEntitySummary} from '../../../../power-ai/timeline-summary/graphql/hooks/use-generate-entity-summary';
import {useRateEntitySummary} from '../../../../power-ai/timeline-summary/graphql/hooks/use-rate-entity-summary';

import {useGetLeadContextSummary} from './use-get-lead-context-summary';

type UseEntitySummaryResult = {
    entitySummary: LeadContextSummary | null | undefined;
    isLoading: boolean;
    error: string | null | undefined;
    regenerateSummary: () => Promise<any>;
    rateSummary: (rating: EntitySummaryRating) => Promise<any>;
    isSavingRating: boolean;
};

export const useLeadSummary = (entityId: string): UseEntitySummaryResult => {
    const {
        leadContextSummary: entitySummary,
        isLoading,
        error,
        refetch,
    } = useGetLeadContextSummary(entityId);
    const {generateEntitySummary} = useGenerateEntitySummary();
    const {rateEntitySummary, isSavingRating} = useRateEntitySummary();

    const regenerateSummary = React.useCallback(async () => {
        await generateEntitySummary(
            {entityId, summaryType: 'ENTITY_CONTEXT'},
            entitySummary ? entitySummary.id : undefined
        );

        // Force refetch to see the updated cache and trigger polling
        await refetch();
    }, [entityId, entitySummary, generateEntitySummary, refetch]);

    const rateSummary = React.useCallback(
        (rating: EntitySummaryRating) => {
            if (!entitySummary || !entitySummary.id) {
                return Promise.resolve();
            }

            return rateEntitySummary({entitySummaryId: entitySummary.id, rating});
        },
        [entitySummary, rateEntitySummary]
    );

    const shouldShowLoading = (entitySummary && entitySummary.status === 'LOADING') || isLoading;

    return {
        entitySummary,
        isLoading: shouldShowLoading,
        error,
        regenerateSummary,
        rateSummary,
        isSavingRating,
    };
};
